import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const SolutionsCTScan = () => {
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm mb-8">
            <Link to="/" className="text-gray-400 hover:text-purple-400">Home</Link>
            <span className="mx-2 text-gray-600">/</span>
            <Link to="/solutions" className="text-gray-400 hover:text-purple-400">Solutions</Link>
            <span className="mx-2 text-gray-600">/</span>
            <span className="text-purple-400">Ultrasound Interpretation</span>
          </div>

          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h1 className="text-4xl font-bold mb-6">
                ZemedicAI Breast Ultrasound
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
                  AI-Assisted Screening
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Our AI-powered breast ultrasound interpretation solution classifies findings as benign, malignant, or normal tissue, giving clinicians a fast, decision-support second read at the point of care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/demo" className="btn-primary px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium inline-block text-center">
                  Try Demo
                </Link>
                <Link to="/contact" className="px-8 py-3 bg-transparent border border-purple-500 text-purple-400 hover:bg-purple-600/10 rounded-lg font-medium inline-block text-center">
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/20 to-indigo-900/20 rounded-xl overflow-hidden border border-gray-800">
              <img
                src="/images/cxr-after.jpg"
                alt="AI Breast Ultrasound Analysis"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">3</div>
              <h3 className="text-xl font-semibold mb-4">Classification Categories</h3>
              <p className="text-gray-300">Benign, malignant, or normal tissue — with attention-based heatmap localization for every result.</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 text-center">
              <div className="text-4xl font-bold text-indigo-400 mb-2">Seconds</div>
              <h3 className="text-xl font-semibold mb-4">Processing Time</h3>
              <p className="text-gray-300">Results delivered in seconds, with Monte-Carlo dropout uncertainty quantification on every screening.</p>
            </div>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">5</div>
              <h3 className="text-xl font-semibold mb-4">Partner Facilities</h3>
              <p className="text-gray-300">Closed clinical validation underway across five partner institutions in Nigeria and Kenya.</p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">How It Works</h2>

            <div className="grid md:grid-cols-2 gap-16 mb-16">
              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  Uncertainty-Aware Screening
                </h3>
                <p className="text-gray-300 mb-4">
                  Every result runs through Monte-Carlo dropout uncertainty quantification — unstable predictions are flagged for mandatory clinician review rather than returned as a confident-sounding but unreliable number.
                </p>
                <p className="text-gray-300 font-medium">
                  This is a clinical-safety property most consumer-facing medical AI tools do not have.
                </p>

                <div className="mt-8 bg-gray-900 rounded-xl overflow-hidden border border-gray-800">
                  <img
                    src="/images/cxr-after.jpg"
                    alt="Uncertainty-aware AI screening"
                    className="w-full h-auto"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  Explainable Localization
                </h3>
                <p className="text-gray-300 mb-4">
                  Attention-based heatmap overlays show clinicians where in the image the model's prediction is grounded — a genuine localization signal derived from the model's own attention weights, not a placeholder.
                </p>
                <p className="text-gray-300">
                  Every screening result is created in a "pending review" state; a clinician must explicitly sign off before it counts as confirmed.
                </p>

                <div className="mt-8 bg-gray-900 rounded-xl overflow-hidden border border-gray-800">
                  <img
                    src="/images/zemedicai-booth.png"
                    alt="Explainable AI localization"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link to="/contact" className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium">
                Contact Us
              </Link>
            </div>
          </div>

          {/* Clinical Validation Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Built on Real Clinical Validation
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <div className="text-3xl font-bold text-purple-400 mb-4">1,000+</div>
                <h3 className="text-xl font-semibold mb-4">Images Processed</h3>
                <p className="text-gray-300">
                  Across chest X-ray, breast ultrasound, diabetic retinopathy, and diabetic foot ulcer modalities from African patient populations to date.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <div className="text-3xl font-bold text-indigo-400 mb-4">80%</div>
                <h3 className="text-xl font-semibold mb-4">Clinician Agreement</h3>
                <p className="text-gray-300">
                  Agreement rate between ZemedicAI's screening output and independent clinical assessment, across a 200-case clinician-adjudicated sample at National Hospital, Abuja.
                </p>
              </div>

              <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
                <div className="text-3xl font-bold text-purple-400 mb-4">5</div>
                <h3 className="text-xl font-semibold mb-4">Partner Institutions</h3>
                <p className="text-gray-300">
                  Closed clinical validation across five hospitals and diagnostic facilities in Nigeria and Kenya.
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-20">
            <p className="text-gray-400 text-sm leading-relaxed">
              ZemedicAI's breast ultrasound interpretation solution is intended to assist clinicians with screening and triage. It is not intended to replace clinical judgment or the expertise of qualified radiologists, and every result requires clinician sign-off before it is treated as confirmed.
            </p>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              For detailed device information, including indications for use, contraindications, and warnings, please consult the user guide prior to use. Features and availability may vary by region.
            </p>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              ZemedicAI, ZemedicAI Enterprise, and the ZemedicAI logo are trademarks of Zema AI Labs, registered in Nigeria.
            </p>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-2xl p-10 text-center border border-purple-500/30">
            <h2 className="text-3xl font-bold mb-4">Extend Diagnostic Access</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contact us to request a demonstration or discuss a clinical validation partnership with ZemedicAI.
            </p>
            <Link
              to="/contact"
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium inline-block"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SolutionsCTScan;
